import React, { Fragment, memo, useEffect, useRef, useState } from 'react';
import { Flex, Carousel } from 'antd-mobile';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { ImgWrap, CommonBtn, FrameWork } from 'pubBiz';
import { createLazyVideoReactComponent, preloadVideoReact } from 'utils/lazyLoad/lazyVideoReact';
import styles from './index.modules.less';

const { Shape } = FrameWork;
const LazyPlayer = createLazyVideoReactComponent('Player');

const defaultProps = {
  className: '',
  loadEnd: false,
  width: 0,
  height: 0,
  mediasList: [],
  changeBg: null,
  onPress: null
};

const mediasListPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    imgInfo: PropTypes.shape({
      imgUrl: PropTypes.string // String 图片地址
    }),
    imgUrlApp: PropTypes.string, // String 图片地址
    imgType: PropTypes.number, // Number 图片类型 0:图片 1:视频
    videoButtonName: PropTypes.string // String 视频按钮名称
  })
);

const propTypes = {
  loadEnd: PropTypes.bool, // 是否加载完成
  className: PropTypes.string, // 自定义容器类名
  width: PropTypes.any, // 图片宽度
  height: PropTypes.any, // 图片高度
  mediasList: mediasListPropTypes, // 数据源
  changeBg: PropTypes.func, // 切换背景
  onPress: PropTypes.func // 点击事件
};

const BannerCell = memo((props) => {
  const {
    className, // String 自定义容器类名
    loadEnd, // Boolean 是否加载完成
    width, // Any 图片宽度
    height, // Any 图片高度
    mediasList, // Array 数据源
    changeBg, // Function 切换背景
    onPress, // Function 点击事件
    styleImg, // 图片样式
    styleBanner // 轮播图样式
  } = props;
  const [index, setIndex] = useState(1);
  const [hasVideo, setHasVideo] = useState(false);
  const dragStateRef = useRef({
    pointerStart: null,
    hasMoved: false,
    preventClickAfterDrag: false,
    preventClickTimer: null
  });

  // 长辈模式不放大 banner：把（可能含 rem 的）高度乘上 --elder-image-scale 抵消根字体放大，
  // 保持原始高度；宽度基于 100vw 不受 rem 影响，无需处理。数字类型高度不含 rem，保持原值。
  const scaledHeight =
    typeof height === 'string' ? `calc(${height} * var(--elder-image-scale, 1))` : height;

  useEffect(() => {
    const nextHasVideo = mediasList.some((v) => v.imgType == 1);

    if (nextHasVideo) {
      setHasVideo(true);
      preloadVideoReact();
    } else {
      setHasVideo(false);
    }
  }, [mediasList]);

  // 记录拖拽起点，给 touch / mouse 共用，避免 PC 下拖拽轮播后又误触发点击跳转。
  const markPointerStart = (pageX, pageY) => {
    dragStateRef.current.pointerStart = { pageX, pageY };
    dragStateRef.current.hasMoved = false;
  };

  // 位移超过阈值就认定为拖拽，后续 click 需要拦截，避免“拖一下就跳链接”。
  const markPointerMove = (pageX, pageY) => {
    const { pointerStart } = dragStateRef.current;
    if (!pointerStart) return;
    const moveX = Math.abs(pageX - pointerStart.pageX);
    const moveY = Math.abs(pageY - pointerStart.pageY);
    if (moveX > 8 || moveY > 8) {
      dragStateRef.current.hasMoved = true;
    }
  };

  // 拖拽结束后的这一个 click 周期内屏蔽点击，避免鼠标松手时被轮播项当成普通点击。
  const markPointerEnd = () => {
    const current = dragStateRef.current;
    current.preventClickAfterDrag = current.hasMoved;
    current.pointerStart = null;
    current.hasMoved = false;
    if (current.preventClickAfterDrag) {
      clearTimeout(current.preventClickTimer);
      current.preventClickTimer = setTimeout(() => {
        dragStateRef.current.preventClickAfterDrag = false;
      }, 120);
    }
  };

  const shouldPreventClick = (e) => {
    if (!dragStateRef.current.preventClickAfterDrag) return false;
    e && e.preventDefault && e.preventDefault();
    e && e.stopPropagation && e.stopPropagation();
    return true;
  };

  // Banner子项
  const renderItem = (v, i) => {
    const { imgUrlApp, imgInfo, imgType, videoButtonName } = v;
    const { imgUrl } = imgInfo;
    return (
      <div key={`banner_${i}`} className={styles.bannerItem} style={styleImg || {}}>
        {/* 视频 */}
        {imgType == 1 && (
          <Fragment>
            <LazyPlayer>
              <source src={imgUrl || ''} />
            </LazyPlayer>
            {videoButtonName && (
              <CommonBtn
                title={videoButtonName}
                className={styles.videoBtn}
                onClick={() => onPress && onPress(v)}
              />
            )}
          </Fragment>
        )}
        {/* 图片 */}
        {imgType != 1 && (
          <div
            onDragStart={(e) => e.preventDefault()}
            onClick={(e) => {
              if (shouldPreventClick(e)) return;
              onPress && onPress(v);
            }}>
            <ImgWrap src={imgUrlApp || imgUrl || ''} mode={'cover'} w={width} h={scaledHeight} />
          </div>
        )}
      </div>
    );
  };

  // 进度条
  const renderProgress = () => {
    return (
      <Flex className={styles.progress}>
        <div
          className={styles.bar}
          style={{
            width: `${(index / mediasList.length) * 100}%`
          }}
        />
      </Flex>
    );
  };

  // 骨架屏
  if (!loadEnd) {
    return <Shape type="square" w={width} h={scaledHeight} />;
  }

  return (
    <div
      className={cx(styles.bannerCellContainer, className)}
      style={styleBanner || {}}
      onMouseDown={(e) => markPointerStart(e.pageX, e.pageY)}
      onMouseMove={(e) => markPointerMove(e.pageX, e.pageY)}
      onMouseUp={markPointerEnd}
      onMouseLeave={markPointerEnd}>
      <Carousel
        style={{ width, height: scaledHeight }}
        autoplay={hasVideo}
        interval={3000}
        dragging
        infinite
        dots={false}
        afterChange={(index) => {
          changeBg && changeBg(mediasList[index].imgUrl || '');
          setIndex(index + 1);
        }}>
        {mediasList.map((v, i) => renderItem(v, i))}
      </Carousel>
      {renderProgress()}
    </div>
  );
});

BannerCell.defaultProps = defaultProps;
BannerCell.propTypes = propTypes;

export default BannerCell;



// WEBPACK FOOTER //
// ./src/pubBiz/CommonCell/BannerCell/index.js